import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Avatar, Text, BadgeNotification, Icon, useTheme, Tooltip } from '@Sage/hoi-poi-ui';

import classnames from 'classnames';
import { PATH_IMAGE } from 'constants/Environment';
import { isBackendFalsy } from '@Sage/ssm-utils/backendScalars';
import { getSrcProductCircleAvatar } from 'utils/getSrcAvatar';
import { getLiteral } from 'utils/getLiteral';
import { isUnsetProductQuantity } from 'models/ProductModel';
import Context from 'managers/Context';
import UtilFormat from 'utils/UtilFormat';
import {
    formatDecimalByConfig,
    DECIMAL_TYPES,
    getDecimalsByConfig,
    normalizeDecimalValue,
    roundDecimal,
} from 'utils/decimal';
import {
    formatDecimal,
    getEffectiveUnitPriceFromRelation,
    getProductBaseUnitRelation,
    getProductUnitRelationSymbol,
} from '@Sage/ssm-utils';

import TruncatedText from 'components/TruncatedText';
import QuantityInput from 'components/QuantityInput';
import { publish, subscribe } from 'lib/EventBuser';
import { UPDATE_PRODUCTS_MODAL_QUANTITY } from 'lib/events';

import './styles.scss';

/**
 * minStep (precision) of the product's base UoM relation. Drives both the +/- step
 * of the QuantityInput and the multiple-of validation for manual entries.
 */
function resolveProductMinStep(baseRelation) {
    const parsed = Number(String(baseRelation?.minStep ?? '').replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function isQuantityMinStepValid(quantity, minStep, decimals) {
    if (!minStep || minStep <= 0) return true;
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty === 0) return true;
    const scale = Math.pow(10, Math.max(0, decimals || 0));
    const scaledQty = Math.round(qty * scale);
    const scaledStep = Math.round(minStep * scale);
    return scaledStep > 0 && scaledQty % scaledStep === 0;
}

function ProductRow({
    data,
    quantityMap,
    invalidMap,
    currencySymbol,
    idEnvironment,
    idCurrency,
    onClickFolder,
}) {
    const formatIdEnvironment =
        idEnvironment && idEnvironment !== '-1' ? idEnvironment : data?.idSucursal;
    const formatIdCurrency = idCurrency || data?.idCurrency;
    const theme = useTheme();
    // Base relation drives the UoM symbol shown alongside stock and the
    // minStep (precision) used for +/- step and manual-entry validation.
    const baseRelation = useMemo(
        () => getProductBaseUnitRelation(data?.productUnitRelations),
        [data?.productUnitRelations],
    );
    const baseUnitSymbol = useMemo(
        () => getProductUnitRelationSymbol(baseRelation),
        [baseRelation],
    );
    const minStep = useMemo(() => resolveProductMinStep(baseRelation), [baseRelation]);
    const quantityStep = minStep > 0 ? minStep : 1;
    // Effective per-unit price mirrors OrdersManager.getLineEffectiveUnitPrice so the
    // modal shows/totals use factor or specialPrice exactly like the salesorderlines table.
    const effectiveUnitPrice = useMemo(
        () => getEffectiveUnitPriceFromRelation(data?.finalprice, baseRelation),
        [data?.finalprice, baseRelation],
    );
    // Same decimal rules as other product quantity fields (schema / env); do not
    // override with UoM minStep so formatting stays under config control.
    const quantityDecimals = useMemo(() => {
        const d = getDecimalsByConfig({
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: formatIdEnvironment,
            entityName: 'PRODUCTS',
            fieldId: 'recommendedQuantity',
        });
        return Number.isInteger(d) && d >= 0 ? d : 0;
    }, [formatIdEnvironment]);
    // Use initialQuantity from data (Summary) or read from quantityMap
    const [quantity, setQuantity] = useState(
        () => data?.initialQuantity ?? quantityMap.current?.[data?.id] ?? 0,
    );

    const isInvalid = useMemo(
        () => !isQuantityMinStepValid(quantity, minStep, quantityDecimals),
        [quantity, minStep, quantityDecimals],
    );

    const innerOnClickFolder = useCallback(() => {
        if (data) onClickFolder(data);
    }, [data, onClickFolder]);

    const onChange = useCallback(
        (quantity, recommendedQuantity) => {
            if (!data) return;
            const normalizedQuantity = normalizeDecimalValue(quantity) ?? 0;
            const normalizedRecommendedQuantity = normalizeDecimalValue(recommendedQuantity) ?? 0;
            const totalQuantity = normalizedQuantity + normalizedRecommendedQuantity;
            const finalQuantity = roundDecimal(totalQuantity, quantityDecimals) ?? 0;
            const invalid = !isQuantityMinStepValid(finalQuantity, minStep, quantityDecimals);

            setQuantity(finalQuantity);
            quantityMap.current[data.id] = finalQuantity;
            if (invalidMap?.current) {
                if (invalid && finalQuantity > 0) {
                    invalidMap.current[data.id] = true;
                } else {
                    delete invalidMap.current[data.id];
                }
            }
            publish(UPDATE_PRODUCTS_MODAL_QUANTITY, {
                product: data,
                value: finalQuantity,
                quantityMap,
                invalid,
                invalidMap,
            });
        },
        [data, invalidMap, minStep, quantityDecimals, quantityMap],
    );

    useEffect(() => {
        if (!data) return;

        const normalizedQuantity = normalizeDecimalValue(quantity);
        if (normalizedQuantity === null || normalizedQuantity === undefined) return;

        const roundedQuantity = roundDecimal(normalizedQuantity, quantityDecimals);
        const normalizedRoundedQuantity = normalizeDecimalValue(roundedQuantity);
        if (
            normalizedRoundedQuantity === null ||
            normalizedRoundedQuantity === undefined ||
            normalizedRoundedQuantity === normalizedQuantity
        ) {
            return;
        }

        setQuantity(normalizedRoundedQuantity);
        quantityMap.current[data.id] = normalizedRoundedQuantity;
        publish(UPDATE_PRODUCTS_MODAL_QUANTITY, {
            product: data,
            value: normalizedRoundedQuantity,
            quantityMap,
            invalidMap,
        });
    }, [data, invalidMap, quantity, quantityDecimals, quantityMap]);

    useEffect(() => {
        if (!data) return;
        return subscribe(UPDATE_PRODUCTS_MODAL_QUANTITY, ({ product, value }) => {
            quantityMap.current[product.id] = value;
            if (data.id !== product.id) return;
            setQuantity(value);
        });
    }, [data, quantityMap]);

    // Keep invalidMap in sync with the current row quantity + minStep so the modal
    // confirm action stays disabled while any row is off-step.
    useEffect(() => {
        if (!data || !invalidMap?.current) return;
        if (isInvalid && quantity > 0) {
            invalidMap.current[data.id] = true;
        } else {
            delete invalidMap.current[data.id];
        }
    }, [data, invalidMap, isInvalid, quantity]);

    // Return null if data is not yet available (lazy load placeholder)
    if (!data) return null;

    const productAvatar = getSrcProductCircleAvatar(data.idThumbnail);
    const showAvatarQuantity = !!quantity;
    const isFolder = !isBackendFalsy(data.isProductFolder);
    const userLocale =
        Context.store?.getState()?.config?.userData?.locale ||
        UtilFormat._localeDecimalFormat ||
        'es-ES';
    const formattedQuantity = formatDecimal(quantity, quantityDecimals, userLocale, null);
    let name = data.name;
    const nameClassName = classnames('fm-add-products-modal__title', {
        'fm-entity-placeholder': !name,
    });
    if (!name) name = getLiteral('label_placeholder_unamed_record');

    return (
        <div className="fm-add-products-modal__cell">
            {isFolder && (
                <div className="fm-add-products-modal__folder" onClick={innerOnClickFolder}>
                    <Icon name="folder" size="large" color={theme.colors.grey[200]} />
                    <Text type="subtitle" isTruncated useTooltip>
                        {data.name}
                    </Text>
                </div>
            )}
            {!isFolder && (
                <div className="fm-add-products-modal__product">
                    <div className="fm-add-products-modal__image">
                        <Avatar
                            type="square"
                            size="big"
                            src={productAvatar.src}
                            placeholder={`${PATH_IMAGE}no_image.png`}
                        />
                        {showAvatarQuantity && (
                            <BadgeNotification
                                className="fm-add-products-modal__image__count"
                                size="tiny"
                            >
                                {quantity > 99 ? '+99' : formattedQuantity}
                            </BadgeNotification>
                        )}
                    </div>
                    <div className="fm-add-products-modal__product-info">
                        <Text className={nameClassName} type="subtitle" isTruncated useTooltip>
                            {name}
                        </Text>
                        <div className="fm-add-products-modal__description">
                            <TruncatedText color={theme.colors.utility.textSecondary}>
                                {data.description}
                            </TruncatedText>
                        </div>
                    </div>
                    <div className="fm-add-products-modal__actions">
                        <div className="fm-add-products-modal__prices">
                            <Text className="fm-add-products-modal__price" type="subtitle">
                                {formatDecimalByConfig({
                                    value: effectiveUnitPrice || 0,
                                    type: DECIMAL_TYPES.CURRENCY_PRICES,
                                    idEnvironment: formatIdEnvironment,
                                    idCurrency: formatIdCurrency,
                                    entityName: 'PRODUCTS',
                                    fieldId: 'finalPrice',
                                    currencySymbol,
                                })}
                            </Text>
                            <Text
                                className="fm-add-products-modal__stock"
                                color={theme.colors.utility.textSecondary}
                            >
                                {getLiteral('label_stock')}:{' '}
                                {isUnsetProductQuantity(data.stock)
                                    ? '-'
                                    : `${formatDecimalByConfig({
                                          value: data.stock,
                                          type: DECIMAL_TYPES.QUANTITY,
                                          idEnvironment: formatIdEnvironment,
                                          entityName: 'PRODUCTS',
                                          fieldId: 'stock',
                                      })}${baseUnitSymbol ? ` ${baseUnitSymbol}` : ''}`}
                            </Text>
                        </div>
                        <div className="fm-add-products-modal__quantity">
                            <div className="fm-add-products-modal__quantity">
                                <QuantityInput
                                    allowDecimals
                                    onChange={onChange}
                                    limitDecimals={quantityDecimals}
                                    quantity={quantity}
                                    step={quantityStep}
                                    isInvalid={isInvalid}
                                />
                            </div>
                            {data.recommendedQuantity === 0 && (
                                <Text type="badges" color={theme.colors.utility.textDisabled}>
                                    {getLiteral('label_recommended')}:{' '}
                                    {formatDecimalByConfig({
                                        value: data.recommendedQuantity,
                                        type: DECIMAL_TYPES.QUANTITY,
                                        idEnvironment: formatIdEnvironment,
                                        entityName: 'PRODUCTS',
                                        fieldId: 'recommendedQuantity',
                                    })}
                                </Text>
                            )}
                            {data.recommendedQuantity > 0 && (
                                <Text
                                    className="fm-add-products-modal__quantity-recommended"
                                    type="badges"
                                    color={theme.colors.utility.textDisabled}
                                >
                                    {getLiteral('label_recommended')}:{' '}
                                    <Tooltip content={getLiteral('action_add')}>
                                        <Text
                                            className="fm-add-products-modal__recommended-total"
                                            type="badges"
                                            color={theme.colors.actionMajor[500]}
                                            onClick={() =>
                                                onChange(quantity, data.recommendedQuantity)
                                            }
                                        >
                                            {formatDecimalByConfig({
                                                value: data.recommendedQuantity,
                                                type: DECIMAL_TYPES.QUANTITY,
                                                idEnvironment: formatIdEnvironment,
                                                entityName: 'PRODUCTS',
                                                fieldId: 'recommendedQuantity',
                                            })}
                                            <Icon
                                                className="fm-add-products-modal__recommended-icon"
                                                name="add"
                                                size="small"
                                                color={theme.colors.actionMajor[500]}
                                            />
                                        </Text>
                                    </Tooltip>
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default memo(ProductRow);
